/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/** 
  All of the routes for the Material Dashboard 2 PRO React are added here,
  You can add a new route, customize the routes and delete the routes here.

  Once you add a new route on this file it will be visible automatically on
  the Sidenav.

  For adding a new route you can follow the existing routes in the routes array.
  1. The `type` key with the `collapse` value is used for a route.
  2. The `type` key with the `title` value is used for a title inside the Sidenav. 
  3. The `type` key with the `divider` value is used for a divider between Sidenav items.
  4. The `name` key is used for the name of the route on the Sidenav.
  5. The `key` key is used for the key of the route (It will help you with the key prop inside a loop).
  6. The `icon` key is used for the icon of the route on the Sidenav, you have to add a node.
  7. The `collapse` key is used for making a collapsible item on the Sidenav that contains other routes
  inside (nested routes), you need to pass the nested routes inside an array as a value for the `collapse` key.
  8. The `route` key is used to store the route location which is used for the react router.
  9. The `href` key is used to store the external links location.
  10. The `title` key is only for the item with the type of `title` and its used for the title text on the Sidenav.
  10. The `component` key is used to store the component of its route.
*/
import NotFound from "layouts/authentication/NotFound";
import FarmIcon from "layouts/Icons/Farm";
import ShowIcon from "layouts/Icons/Show";
import HorseIcon from "layouts/Icons/Horse";
import CertificateIcon from "layouts/Icons/Certificate";
import MDAvatar from "components/MDAvatar";
import endpointMaker from "appredux/endpoints";
import { lazy, Suspense } from "react";
import BasePageLoader from "components/Loaders/BasePageLoader";
import { Route } from "types/Routes";
import { FARM_ROLES } from "api/farm-users/types";
import UsersIcon from "components/Icons/UsersIcon";
import { User } from "appredux/model/user";
const DataTables = lazy(() => import("layouts/horses/all-horses"));
const EditHorseProfile = lazy(() => import("layouts/horses/edit-profile/EditHorseScreen"));
const AddHorseProfile = lazy(() => import("layouts/horses/add-horse/AddHorseScreen"));
const HorseProfile = lazy(() => import("layouts/horses/horse-profile"));
const HeathProof = lazy(() => import("layouts/horses/health-proof/HeathProof"));
const UploadedProofs = lazy(() => import("layouts/health-proof/UploadedProofs"));
const CreatePass = lazy(() => import("layouts/eventPass/create-pass"));
const AddShow = lazy(() => import("layouts/shows/add-show"));
const SelectShow = lazy(() => import("layouts/shows/select-show"));
const AddCertificate = lazy(() => import("layouts/horses/add-certificate"));
const HealthDeclaration = lazy(() => import("layouts/eventPass/health-declaration"));
const ViewShow = lazy(() => import("layouts/shows/edit-show"));
const AllPasses = lazy(() => import("layouts/eventPass/all-passes"));
const ViewPass = lazy(() => import("layouts/eventPass/view-pass"));
const SubmitPass = lazy(() => import("layouts/eventPass/submit-pass"));
const Checkout = lazy(() => import("layouts/authentication/StripeEmbeddedCheckout"));
const PriceList = lazy(() => import("layouts/authentication/PriceList"));
const PricingPage = lazy(() => import("layouts/pages/pricing-page"));
const AddCoggins = lazy(() => import("layouts/horses/add-coggins"));
const AddVaccination = lazy(() => import("layouts/horses/add-vaccination"));
const AddTemperature = lazy(() => import("layouts/horses/add-temperature"));
const TempDataTables = lazy(() => import("layouts/horses/temperatures"));
const StallHistoryDataTables = lazy(() => import("layouts/horses/stall-history"));
const SignInCover = lazy(() => import("layouts/authentication/sign-in/cover"));
const SignUpCover = lazy(() => import("layouts/authentication/sign-up"));
const Settings = lazy(() => import("layouts/pages/account/settings"));
const AllShows = lazy(() => import("layouts/shows/all-shows"));
const ResetPassword = lazy(() => import("layouts/authentication/reset-password/cover"));
const SetPassword = lazy(() => import("layouts/authentication/reset-password/set-password"));
const MembershipCover = lazy(() => import("layouts/authentication/membership"));
const LinkDetails = lazy(() => import("layouts/authentication/linkDetails"));
const USEFSignIn = lazy(() => import("layouts/authentication/usef-sign-in"));
const LastDetails = lazy(() => import("layouts/authentication/lastDetails"));
const ImportFromUsef = lazy(() => import("layouts/horses/import-from-usef/ImportFromUsef"));
const SelectActiveHorses = lazy(() => import("layouts/horses/select-active-horses"));
const ContinuePayment = lazy(() => import("layouts/subscription/continue-payment"));
const SuccessPayment = lazy(() => import("layouts/subscription/success"));
const VerifyEmail = lazy(() => import("layouts/authentication/verify-email"));
const Logout = lazy(() => import("layouts/authentication/logout"));
const TransferHorse = lazy(() => import("layouts/horses/transfer-horse"));
const SelectHorsesForEventPass = lazy(() => import("layouts/shows/add-show/AddShow"));
const AdditionalShows = lazy(() => import("layouts/shows/additional-shows"));
const ConfirmDates = lazy(() => import("layouts/shows/confirm-dates"));
const Trainers = lazy(() => import("layouts/shows/trainers"));
const ChangeHorseNameRequest = lazy(
  () => import("layouts/horses/change-horse-name/ChangeHorseNameRequest")
);
const HorseChangeRequestsList = lazy(
  () => import("layouts/horses/change-horse-name/HorseChangeRequestsList")
);
const ManageSubscriptionFromApp = lazy(
  () => import("layouts/authentication/ManageSubscriptionFromApp/ManageSubscriptionFromApp")
);
const AuthByToken = lazy(() => import("layouts/authentication/auth-by-token"));
const InstantEventPassClaimAccount = lazy(
  () => import("layouts/pages/account/instant-eventpass-claim-account")
);
const UserManagement = lazy(() => import("layouts/user-management/UserManagement"));
const SetUpAccount = lazy(() => import("layouts/pages/account/set-up-account"));
const InvitationDecision = lazy(() => import("layouts/pages/account/invitation-decision"));
const EventPassHorseList = lazy(() => import("layouts/eventPass/EventPassHorseList"));

const profilePicture = endpointMaker.api.userPhoto(
  localStorage.getItem("userId"),
  localStorage.getItem("farm"),
  0
);

const routes: Route[] = [
  {
    type: "collapse",
    name: "XXXXXXXXX",
    key: "kevin-corley",
    menu: true,
    icon: <MDAvatar src={profilePicture} alt="Brooklyn Alice" size="sm" />,
    collapse: [
      {
        name: "Profile",
        key: "profile-settings",
        menu: false,
        route: "/profile",
        head: {
          title: "View Profile",
        },
        private: true,
        component: (
          <Suspense fallback={<BasePageLoader />}>
            <Settings />
          </Suspense>
        ),
      },
      {
        name: "Profile",
        key: "profile-settings-link",
        menu: true,
        route: "/profile#profile",
      },
      {
        name: "USEF Account",
        key: "usef-account",
        menu: true,
        route: "/profile#usef-account",
      },
      {
        name: "Subscription",
        key: "subscription",
        menu: true,
        route: "/profile#subscription",
      },
      {
        name: "Manage Subscription",
        key: "manage-subscription-from-app",
        menu: false,
        route: "/manage",
        component: (
          <Suspense fallback={<BasePageLoader layout="page" />}>
            <ManageSubscriptionFromApp />
          </Suspense>
        ),
      },
      {
        name: "Logout",
        key: "logout",
        menu: true,
        href: "/authentication/sign-in/cover",
        target: "_self",
      },
      {
        name: "sign-in",
        key: "sign-in",
        menu: false,
        route: "/authentication/sign-in/cover",
        head: {
          title: "EventPass Sign In",
        },
        component: (
          <Suspense fallback={<BasePageLoader layout="page" />}>
            <SignInCover />
          </Suspense>
        ),
      },
    ],
  },
  {
    name: "Sign Up",
    key: "signup",
    menu: false,
    route: "/authentication/sign-up",
    head: {
      title: "EventPass Sign Up",
    },
    component: (
      <Suspense fallback={<BasePageLoader layout="page" />}>
        <SignUpCover />
      </Suspense>
    ),
  },
  {
    name: "Reset password",
    key: "resetpw",
    menu: false,
    route: "/authentication/reset-password",
    head: {
      title: "EventPass Reset Password",
    },
    component: (
      <Suspense fallback={<BasePageLoader layout="page" />}>
        <ResetPassword />
      </Suspense>
    ),
  },
  {
    name: "Set New password",
    key: "setpw",
    menu: false,
    route: "/authentication/set-new-password/:email/:token",
    component: (
      <Suspense fallback={<BasePageLoader layout="page" />}>
        <SetPassword />
      </Suspense>
    ),
  },
  {
    name: "Membership",
    key: "membership",
    menu: false,
    route: "/authentication/membership/:token",
    component: (
      <Suspense fallback={<BasePageLoader layout="page" />}>
        <MembershipCover />
      </Suspense>
    ),
  },
  {
    name: "Last Details",
    key: "lastDetails",
    menu: false,
    route: "/authentication/lastDetails",
    component: (
      <Suspense fallback={<BasePageLoader layout="page" />}>
        <LastDetails />
      </Suspense>
    ),
  },
  {
    name: "Link Details",
    key: "linkDetails",
    menu: false,
    private: true,
    route: "/authentication/linkDetails",
    component: (
      <Suspense fallback={<BasePageLoader layout="page" />}>
        <LinkDetails />
      </Suspense>
    ),
  },
  {
    name: "Account Linked",
    key: "accountLinked",
    menu: false,
    route: "/authentication/usef-sign-in",
    component: (
      <Suspense fallback={<BasePageLoader layout="page" />}>
        <USEFSignIn />
      </Suspense>
    ),
  },

  {
    name: "Auth By Token",
    key: "auth_by_token",
    menu: false,
    route: "/auth-by-token/",
    component: (
      <Suspense fallback={<BasePageLoader layout="page" />}>
        <AuthByToken />
      </Suspense>
    ),
  },
  {
    name: "HorseOverview",
    key: "horseoverview",
    menu: false,
    private: true,
    route: "/horses/:microchip",
    head: {
      title: "View Horse",
    },
    component: (
      <Suspense fallback={<BasePageLoader />}>
        <HorseProfile />
      </Suspense>
    ),
  },
  {
    name: "AddHorse",
    key: "addhorse",
    menu: false,
    private: true,
    route: "/horses/add-horse",
    head: {
      title: "Add Horse",
    },
    component: (
      <Suspense fallback={<BasePageLoader />}>
        <AddHorseProfile />
      </Suspense>
    ),
  },
  {
    name: "EditProfile",
    key: "editprofile",
    menu: false,
    private: true,
    route: "/horses/:microchip/edit-profile",
    head: {
      title: "Edit Horse Profile",
    },
    component: (
      <Suspense fallback={<BasePageLoader />}>
        <EditHorseProfile />
      </Suspense>
    ),
  },
  {
    name: "Transfer Horse",
    key: "transferhorse",
    menu: false,
    private: true,
    route: "/horses/:microchip/transfer",
    head: {
      title: "Transfer Horse",
    },
    component: (
      <Suspense fallback={<BasePageLoader />}>
        <TransferHorse />
      </Suspense>
    ),
  },
  { type: "divider", key: "divider-0", name: "divider-0", menu: true },
  {
    type: "collapse",
    name: "YYYYYYYYY",
    key: "farm-name",
    menu: true,
    farmList: true,
    icon: <FarmIcon size={25} color="white" />,
    // @ts-ignore
    collapse: [{}],
  },
  {
    type: "collapse",
    name: "Horses",
    key: "horses",
    menu: true,
    icon: <HorseIcon size={25} color="white" />,
    collapse: [
      {
        name: "All Horses",
        key: "allHorses",
        private: true,
        route: "/horses",
        head: {
          title: "Horses",
        },
        component: (
          <Suspense fallback={<BasePageLoader />}>
            <DataTables />
          </Suspense>
        ),
      },
      {
        name: "Temperatures",
        key: "temperatures",
        private: true,
        menu: false,
        route: "/horses/temperatures",
        component: (
          <Suspense fallback={<BasePageLoader />}>
            <TempDataTables />
          </Suspense>
        ),
      },
      {
        name: "Stall History",
        key: "stallHistory",
        private: true,
        menu: false,
        route: "/horses/stall-history",
        component: (
          <Suspense fallback={<BasePageLoader />}>
            <StallHistoryDataTables />
          </Suspense>
        ),
      },
      {
        name: "Change Horse Name",
        key: "changeHorseNameRequest",
        private: true,
        menu: false,
        route: "/horses/change-horse-name/:microchip",
        head: {
          title: "Change Horse Registered Name",
        },
        component: (
          <Suspense fallback={<BasePageLoader />}>
            <ChangeHorseNameRequest />
          </Suspense>
        ),
      },
      {
        name: "Name Change Requests",
        key: "horseChangeRequestsList",
        private: true,
        menu: false,
        route: "/horses/change-horse-name/requests",
        head: {
          title: "Horse Name Change Requests",
        },
        component: (
          <Suspense fallback={<BasePageLoader />}>
            <HorseChangeRequestsList />
          </Suspense>
        ),
      },
    ],
  },
  {
    name: "Change Farm",
    key: "changeFarm",
    private: true,
    menu: false,
    route: "/horses/changeFarm/:farm",
    component: (
      <Suspense fallback={<BasePageLoader />}>
        <DataTables />
      </Suspense>
    ),
  },
  {
    type: "collapse",
    name: "Health Proofs",
    key: "health-proofs",
    menu: true,
    icon: <CertificateIcon size={25} color="white" />,
    collapse: [
      {
        name: "Upload Proof",
        key: "healthproof",
        private: true,
        route: "/health-proofs/upload/",
        head: {
          title: "Upload Document",
        },
        component: (
          <Suspense fallback={<BasePageLoader />}>
            <HeathProof />
          </Suspense>
        ),
      },
      {
        name: "All Uploaded Proofs",
        key: "All Uploaded Proofs",
        private: true,
        route: "/health-proofs/list-of-proofs",
        head: {
          title: "Uploaded Proofs",
        },
        component: (
          <Suspense fallback={<BasePageLoader />}>
            <UploadedProofs />
          </Suspense>
        ),
      },
      {
        name: "Add Vaccination cert",
        key: "vacc",
        private: false,
        menu: false,
        route: "/horses/add-vaccination/",
        component: (
          <Suspense fallback={<BasePageLoader />}>
            <AddVaccination />
          </Suspense>
        ),
      },
      {
        name: "Add Coggins cert",
        key: "coggins",
        private: false,
        menu: false,
        route: "/horses/add-coggins/",
        component: (
          <Suspense fallback={<BasePageLoader />}>
            <AddCoggins />
          </Suspense>
        ),
      },
      {
        name: "Add Health cert",
        key: "addcertificate",
        menu: false,
        route: "/horses/add-certificate/",
        component: (
          <Suspense fallback={<BasePageLoader />}>
            <AddCertificate />
          </Suspense>
        ),
      },
      {
        name: "Certify Temperatures",
        key: "addtemperature",
        menu: false,
        private: true,
        route: "/horses/add-temperature/",
        component: (
          <Suspense fallback={<BasePageLoader />}>
            <AddTemperature />
          </Suspense>
        ),
      },
      {
        name: "Checkout",
        key: "checkout",
        menu: false,
        route: "/authentication/checkout/",
        component: (
          <Suspense fallback={<BasePageLoader />}>
            <Checkout />
          </Suspense>
        ),
      },
      {
        name: "PriceList",
        key: "PriceList",
        menu: false,
        route: "/authentication/priceList/",
        component: (
          <Suspense fallback={<BasePageLoader />}>
            <PricingPage />
          </Suspense>
        ),
      },
    ],
  },
  {
    type: "collapse",
    name: "Shows",
    key: "shows",
    menu: true,
    icon: <ShowIcon size={25} color="white" />,
    collapse: [
      {
        name: "All Shows",
        key: "allshows",
        private: true,
        menu: false,
        route: "/shows",
        component: (
          <Suspense fallback={<BasePageLoader />}>
            <AllShows />
          </Suspense>
        ),
      },
      {
        name: "Event Passes",
        key: "eventpass",
        private: true,
        route: "/eventPasses",
        component: (
          <Suspense fallback={<BasePageLoader />}>
            <AllPasses />
          </Suspense>
        ),
      },
      {
        name: "Add Show to Attend",
        key: "eventPassSelectShow",
        private: true,
        menu: true,
        route: "/eventPasses/select-show/",
        head: {
          title: "Add Show to Attend",
        },
        component: (
          <Suspense fallback={<BasePageLoader />}>
            <SelectShow />
          </Suspense>
        ),
      },
    ],
  },
  {
    name: "Create Pass",
    key: "createpass",
    private: true,
    menu: false,
    route: "/horses/:microchip/select-show/create-pass",
    component: (
      <Suspense fallback={<BasePageLoader />}>
        <CreatePass />
      </Suspense>
    ),
  },
  {
    name: "Create Pass",
    key: "createpass",
    private: true,
    menu: false,
    route: "/eventPasses/create-pass",
    head: {
      title: "Create EventPass",
    },
    component: (
      <Suspense fallback={<BasePageLoader />}>
        <CreatePass />
      </Suspense>
    ),
  },
  {
    name: "View Pass",
    key: "viewpass",
    private: true,
    menu: false,
    route: "/eventPasses/:eventPassId/view-pass",
    head: {
      title: "View EventPass",
    },
    component: (
      <Suspense fallback={<BasePageLoader />}>
        <ViewPass />
      </Suspense>
    ),
  },
  {
    name: "Select Show",
    key: "selectshow",
    private: true,
    menu: false,
    route: "/eventPasses/select-show/:eventPassId",
    component: (
      <Suspense fallback={<BasePageLoader />}>
        <EventPassHorseList />
      </Suspense>
    ),
  },
  {
    name: "View Show",
    key: "viewshow",
    private: true,
    menu: false,
    route: "/shows/view-show/:showId",
    component: (
      <Suspense fallback={<BasePageLoader />}>
        <ViewShow />
      </Suspense>
    ),
  },
  {
    name: "Add Show",
    key: "addshow",
    private: true,
    menu: false,
    route: "/horses/:microchip/select-show",
    head: {
      title: "Create EventPass",
    },
    component: (
      <Suspense fallback={<BasePageLoader />}>
        <AddShow />
      </Suspense>
    ),
  },
  {
    name: "Edit Show",
    key: "editshow",
    private: true,
    menu: false,
    route: "/shows/edit-show/:eventPassId",
    component: (
      <Suspense fallback={<BasePageLoader />}>
        <AddShow />
      </Suspense>
    ),
  },
  {
    name: "Add Show",
    key: "addshow",
    private: true,
    menu: false,
    route: "/eventPasses/add-show/",
    head: {
      title: "Create EventPass",
    },
    component: (
      <Suspense fallback={<BasePageLoader />}>
        <AddShow />
      </Suspense>
    ),
  },
  {
    name: "Select Horses for EventPass",
    key: "addshow",
    private: true,
    menu: false,
    route: "/shows/add-show/select-horses",
    head: {
      title: "Select Horses for EventPass",
    },
    component: (
      <Suspense fallback={<BasePageLoader />}>
        <SelectHorsesForEventPass />
      </Suspense>
    ),
  },
  {
    name: "Additional Shows",
    key: "additionalshows",
    private: true,
    menu: false,
    route: "/shows/add-show/additional-shows",
    head: {
      title: "Additional Shows",
    },
    component: (
      <Suspense fallback={<BasePageLoader />}>
        <AdditionalShows />
      </Suspense>
    ),
  },
  {
    name: "Confirm Dates",
    key: "confirmDates",
    private: true,
    menu: false,
    route: "/shows/add-show/confirm-dates",
    head: {
      title: "Create EventPass",
    },
    component: (
      <Suspense fallback={<BasePageLoader />}>
        <ConfirmDates />
      </Suspense>
    ),
  },
  {
    name: "Trainers",
    key: "trainers",
    private: true,
    menu: false,
    route: "/shows/add-show/trainers",
    head: {
      title: "Create EventPass",
    },
    component: (
      <Suspense fallback={<BasePageLoader />}>
        <Trainers />
      </Suspense>
    ),
  },
  {
    name: "Add Health Certificate",
    key: "addcertificate",
    menu: false,
    private: true,
    route: "/horses/:microchip/add-certificate",
    component: (
      <Suspense fallback={<BasePageLoader />}>
        <AddCertificate />
      </Suspense>
    ),
  },
  {
    name: "Health Declaration",
    key: "healthdeclaration",
    menu: false,
    private: true,
    route: "/eventPass/health-declaration/:eventPassId",
    component: (
      <Suspense fallback={<BasePageLoader />}>
        <HealthDeclaration />
      </Suspense>
    ),
  },
  {
    name: "Create Event Pass",
    key: "passSubmitted",
    menu: false,
    private: true,
    route: "/eventPass/submit-pass",
    component: (
      <Suspense fallback={<BasePageLoader />}>
        <SubmitPass />
      </Suspense>
    ),
  },
  {
    name: "Add Vaccination",
    key: "addvaccination",
    menu: false,
    private: true,
    route: "/horses/:microchip/add-vaccination",
    component: (
      <Suspense fallback={<BasePageLoader />}>
        <AddVaccination />
      </Suspense>
    ),
  },
  {
    name: "Add Coggins",
    key: "addcoggins",
    menu: false,
    private: true,
    route: "/horses/:microchip/add-coggins",
    component: (
      <Suspense fallback={<BasePageLoader />}>
        <AddCoggins />
      </Suspense>
    ),
  },
  {
    name: "Add Temperature",
    key: "addtemperature",
    menu: false,
    private: true,
    route: "/horses/:microchip/add-temperature",
    component: (
      <Suspense fallback={<BasePageLoader />}>
        <AddTemperature />
      </Suspense>
    ),
  },
  {
    name: "Import Horses From USEF",
    key: "import_horses_from_usef",
    menu: false,
    private: true,
    route: "/horses/import-horses-from-usef",
    component: (
      <Suspense fallback={<BasePageLoader />}>
        <ImportFromUsef />
      </Suspense>
    ),
  },
  {
    name: "Select Active Horses",
    key: "select_active_horses",
    menu: false,
    private: true,
    route: "/horses/select-active-horses",
    component: (
      <Suspense fallback={<BasePageLoader />}>
        <SelectActiveHorses />
      </Suspense>
    ),
  },

  {
    name: "Continue Payment",
    key: "continue_payment",
    menu: false,
    private: true,
    route: "/subscription/continue-payment",
    component: (
      <Suspense fallback={<BasePageLoader layout="page" />}>
        <ContinuePayment />
      </Suspense>
    ),
  },
  {
    name: "Success Payment",
    key: "success_payment",
    menu: false,
    route: "/subscription/success",
    component: (
      <Suspense fallback={<BasePageLoader layout="page" />}>
        <SuccessPayment />
      </Suspense>
    ),
  },
  {
    name: "Verify Email",
    key: "verify_email",
    menu: false,
    route: "/verify-email/success",
    component: (
      <Suspense fallback={<BasePageLoader layout="page" />}>
        <VerifyEmail />
      </Suspense>
    ),
  },
  {
    name: "Instant EventPass Claim Account",
    key: "instant_eventpass_claim_account",
    menu: false,
    private: true,
    route: "/instant-eventpass-claim-account/",
    component: (
      <Suspense fallback={<BasePageLoader layout="page" />}>
        <InstantEventPassClaimAccount />
      </Suspense>
    ),
  },
  {
    type: "collapse",
    name: "Users",
    key: "venues_and_users",
    menu: true,
    icon: <UsersIcon />,
    visible: (user: User) =>
      user?.farm_role === FARM_ROLES.OWNER || user?.farm_role === FARM_ROLES.ADMIN,
    collapse: [
      {
        name: "Users and access",
        key: "user_management",
        private: true,
        route: "/user-management",
        head: {
          title: "Users and access",
        },
        component: (
          <Suspense fallback={<BasePageLoader />}>
            <UserManagement />
          </Suspense>
        ),
      },
    ],
  },
  {
    name: "Set Up Account",
    key: "set_up_account",
    menu: false,
    private: true,
    route: "/set-up-account/",
    component: (
      <Suspense fallback={<BasePageLoader layout="page" />}>
        <SetUpAccount />
      </Suspense>
    ),
  },
  {
    name: "You’ve been invited",
    key: "invitation_decision",
    menu: false,
    private: true,
    route: "/invitation-decision/",
    component: (
      <Suspense fallback={<BasePageLoader layout="page" />}>
        <InvitationDecision />
      </Suspense>
    ),
  },
  {
    name: "404",
    key: "notfound",
    menu: false,
    route: "/404",
    component: <NotFound />,
  },
];

export default routes;
